import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ListTodo, Plus, Search, Filter, CheckCircle2, Clock, AlertTriangle,
  Calendar, User, Tag, MessageSquare, MoreHorizontal, Trash2, Edit,
  ChevronRight, RefreshCw, Loader2, ArrowUp, ArrowDown, Target
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const TASK_TYPES = [
  { value: 'general', label: 'General', color: 'bg-slate-100 text-slate-700' },
  { value: 'crm', label: 'CRM', color: 'bg-blue-100 text-blue-700' },
  { value: 'sales', label: 'Sales', color: 'bg-green-100 text-green-700' },
  { value: 'marketing', label: 'Marketing', color: 'bg-purple-100 text-purple-700' },
  { value: 'support', label: 'Support', color: 'bg-orange-100 text-orange-700' },
  { value: 'finance', label: 'Finance', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'hr', label: 'HR', color: 'bg-pink-100 text-pink-700' },
  { value: 'operations', label: 'Operations', color: 'bg-indigo-100 text-indigo-700' }
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600', icon: ArrowDown },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-600', icon: null },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-600', icon: ArrowUp },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-600', icon: AlertTriangle }
];

const STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-slate-100 text-slate-600' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-600' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-600' },
  { value: 'blocked', label: 'Blocked', color: 'bg-red-100 text-red-600' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-slate-200 text-slate-500' }
];

const OperationsTasksManager = () => {
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    task_type: '',
    assigned_to: '',
    search: '',
    my_tasks_only: false
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    task_type: 'general',
    priority: 'medium',
    assigned_to: '',
    due_date: '',
    tags: ''
  });

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/operations/tasks/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.task_type) params.append('task_type', filters.task_type);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
      if (filters.search) params.append('search', filters.search);
      if (filters.my_tasks_only) params.append('my_tasks_only', 'true');
      params.append('include_completed', 'true');

      const res = await axios.get(`${API}/api/operations/tasks?${params}`, { headers });
      setTasks(res.data.tasks || []);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  }, [filters]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/users/team`, { headers });
      setTeamMembers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchTasks(), fetchTeamMembers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchTasks, fetchTeamMembers]);

  useEffect(() => {
    if (!loading) {
      fetchTasks();
    }
  }, [filters, fetchTasks, loading]);

  const createTask = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };
      await axios.post(`${API}/api/operations/tasks`, payload, { headers });
      toast.success('Task created');
      setCreateDialogOpen(false);
      setForm({ title: '', description: '', task_type: 'general', priority: 'medium', assigned_to: '', due_date: '', tags: '' });
      fetchDashboard();
      fetchTasks();
    } catch (err) {
      toast.error('Failed to create task');
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`${API}/api/operations/tasks/${taskId}`, { status }, { headers });
      toast.success('Task updated');
      fetchDashboard();
      fetchTasks();
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/api/operations/tasks/${taskId}`, { headers });
      toast.success('Task deleted');
      fetchDashboard();
      fetchTasks();
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  const bulkUpdateStatus = async (status) => {
    if (selectedTasks.length === 0) return;
    try {
      await axios.post(`${API}/api/operations/tasks/bulk/status`, {
        task_ids: selectedTasks,
        status
      }, { headers });
      toast.success(`Updated ${selectedTasks.length} tasks`);
      setSelectedTasks([]);
      fetchDashboard();
      fetchTasks();
    } catch (err) {
      toast.error('Failed to update tasks');
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
  const getStatusInfo = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];
  const getTypeInfo = (type) => TASK_TYPES.find(t => t.value === type) || TASK_TYPES[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') return false;
    return new Date(task.due_date) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ListTodo className="h-6 w-6" />
            Task Management
          </h2>
          <p className="text-muted-foreground">Centralized task management across all modules</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { fetchDashboard(); fetchTasks(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-task-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="list">All Tasks</TabsTrigger>
          <TabsTrigger value="my_tasks">My Tasks</TabsTrigger>
          <TabsTrigger value="overdue">Overdue</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{dashboard?.stats?.total || 0}</div>
                <p className="text-xs text-muted-foreground">Total Tasks</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-600">{dashboard?.stats?.pending || 0}</div>
                <p className="text-xs text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-600">{dashboard?.stats?.in_progress || 0}</div>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">{dashboard?.stats?.completed || 0}</div>
                <p className="text-xs text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{dashboard?.stats?.overdue || 0}</div>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-purple-600">{dashboard?.stats?.my_tasks || 0}</div>
                <p className="text-xs text-muted-foreground">My Tasks</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-500">{dashboard?.stats?.critical || 0}</div>
                <p className="text-xs text-muted-foreground">Critical</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-orange-500">{dashboard?.stats?.high_priority || 0}</div>
                <p className="text-xs text-muted-foreground">High Priority</p>
              </CardContent>
            </Card>
          </div>

          {/* Tasks by Type */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tasks by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(dashboard?.by_type || {}).map(([type, count]) => {
                    const typeInfo = getTypeInfo(type);
                    return (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                        </div>
                        <span className="font-medium">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Due Soon</CardTitle>
                <CardDescription>Tasks due in the next 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {(dashboard?.due_soon || []).map(task => (
                      <div key={task.id} className="flex items-center justify-between p-2 rounded border">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{task.title}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(task.due_date)}
                          </p>
                        </div>
                        <Badge className={getPriorityInfo(task.priority).color}>
                          {task.priority}
                        </Badge>
                      </div>
                    ))}
                    {(dashboard?.due_soon || []).length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No tasks due soon</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Recent Tasks */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(dashboard?.recent_tasks || []).slice(0, 5).map(task => (
                  <div key={task.id} className="flex items-center justify-between p-3 rounded border hover:bg-slate-50">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Checkbox
                        checked={task.status === 'completed'}
                        onCheckedChange={() => updateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {task.assigned_to_name && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {task.assigned_to_name}
                            </span>
                          )}
                          {task.due_date && (
                            <span className={`flex items-center gap-1 ${isOverdue(task) ? 'text-red-500' : ''}`}>
                              <Clock className="h-3 w-3" />
                              {formatDate(task.due_date)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getTypeInfo(task.task_type).color}>{task.task_type}</Badge>
                      <Badge className={getPriorityInfo(task.priority).color}>{task.priority}</Badge>
                    </div>
                  </div>
                ))}
                {(dashboard?.recent_tasks || []).length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No tasks yet. Create your first task!</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Tasks / My Tasks / Overdue Tabs */}
        {['list', 'my_tasks', 'overdue'].map(tab => (
          <TabsContent key={tab} value={tab} className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search tasks..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.priority || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, priority: v === "all" ? "" : v }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.task_type || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, task_type: v === "all" ? "" : v }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {TASK_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTasks.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline">
                          Bulk Actions ({selectedTasks.length})
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => bulkUpdateStatus('completed')}>
                          <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Complete
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => bulkUpdateStatus('in_progress')}>
                          <Clock className="h-4 w-4 mr-2" /> Mark In Progress
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => bulkUpdateStatus('pending')}>
                          <Target className="h-4 w-4 mr-2" /> Mark Pending
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Task List */}
            <Card>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {tasks
                    .filter(t => {
                      if (tab === 'my_tasks') {
                        const user = JSON.parse(localStorage.getItem('user') || '{}');
                        return t.assigned_to === user.id;
                      }
                      if (tab === 'overdue') return isOverdue(t);
                      return true;
                    })
                    .map(task => (
                    <div key={task.id} className={`flex items-center justify-between p-3 rounded border hover:bg-slate-50 ${isOverdue(task) ? 'border-red-200 bg-red-50' : ''}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Checkbox
                          checked={selectedTasks.includes(task.id)}
                          onCheckedChange={() => toggleTaskSelection(task.id)}
                        />
                        <Checkbox
                          checked={task.status === 'completed'}
                          onCheckedChange={() => updateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {task.assigned_to_name && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {task.assigned_to_name}
                              </span>
                            )}
                            {task.due_date && (
                              <span className={`flex items-center gap-1 ${isOverdue(task) ? 'text-red-500 font-medium' : ''}`}>
                                <Clock className="h-3 w-3" />
                                {formatDate(task.due_date)}
                                {isOverdue(task) && ' (Overdue)'}
                              </span>
                            )}
                            {task.tags?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Tag className="h-3 w-3" />
                                {task.tags.slice(0, 2).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getTypeInfo(task.task_type).color}>{task.task_type}</Badge>
                        <Badge className={getPriorityInfo(task.priority).color}>{task.priority}</Badge>
                        <Badge className={getStatusInfo(task.status).color}>{task.status}</Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditTask(task)}>
                              <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteTask(task.id)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No tasks found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Create Task Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter task title"
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter task description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.task_type} onValueChange={(v) => setForm(prev => ({ ...prev, task_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm(prev => ({ ...prev, assigned_to: v === "unassigned" ? "" : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input
                value={form.tags}
                onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="e.g., urgent, follow-up, meeting"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={createTask} data-testid="submit-task-btn">Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationsTasksManager;
